// src/utils/clientDiagnostics.js
// In-memory ring buffer of recent client-side problems (console errors/warnings, uncaught
// errors, rejected promises, error toasts). Nothing is sent anywhere automatically — the
// onboarding feedback bubble attaches a snapshot only when the user submits a report and
// leaves "Include technical details" ticked. Lives in memory only; cleared on reload.

const MAX_ENTRIES = 40;
const MAX_LEN = 600;
const buffer = [];
let installed = false;

const redact = (text) => String(text)
  .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{5,}/g, '[jwt]')
  .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [redacted]')
  .replace(/([?&](?:token|code|otp|password|key)=)[^&\s"']+/gi, '$1[redacted]')
  .slice(0, MAX_LEN);

const stringify = (arg) => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === 'string') return arg;
  try { return JSON.stringify(arg); } catch { return String(arg); }
};

export function recordClientEvent(level, ...args) {
  try {
    buffer.push({ level, msg: redact(args.map(stringify).join(' ')), t: new Date().toISOString() });
    if (buffer.length > MAX_ENTRIES) buffer.shift();
  } catch { /* never let diagnostics break the app */ }
}

export function getClientDiagnostics() {
  return buffer.slice();
}

export function installClientDiagnostics() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  ['error', 'warn'].forEach((level) => {
    const original = console[level];
    console[level] = (...args) => {
      recordClientEvent(level, ...args);
      original.apply(console, args);
    };
  });

  window.addEventListener('error', (e) => {
    recordClientEvent('error', e.message || 'Uncaught error', e.filename ? `@ ${e.filename}:${e.lineno}` : '');
  });
  window.addEventListener('unhandledrejection', (e) => {
    recordClientEvent('error', 'Unhandled rejection:', e.reason);
  });
}

installClientDiagnostics();
